import React, { useEffect, useRef, forwardRef } from 'react';
import { motion } from 'framer-motion';
import Typed from 'typed.js';

interface ChatMessageProps {
  content: string;
  type: 'user' | 'ai';
  isTyping?: boolean;
  messageId: string;
  onTypingComplete?: () => void;
}

export const ChatMessage = forwardRef<HTMLDivElement, ChatMessageProps>(({ 
  content, 
  type, 
  isTyping, 
  messageId, 
  onTypingComplete 
}, ref) => {
  const typedRef = useRef<Typed | null>(null);
  const messageContentRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef(content);

  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  useEffect(() => {
    if (isTyping && type === 'ai' && messageContentRef.current) {
      if (typedRef.current) {
        typedRef.current.destroy();
      }

      typedRef.current = new Typed(messageContentRef.current, {
        strings: [contentRef.current],
        typeSpeed: 20,
        showCursor: true,
        cursorChar: '|',
        onComplete: () => {
          onTypingComplete?.();
          if (messageContentRef.current) {
            messageContentRef.current.innerHTML = contentRef.current;
          }
        }
      });

      return () => {
        if (typedRef.current) {
          typedRef.current.destroy();
        }
      };
    } else if (!isTyping && messageContentRef.current) {
      messageContentRef.current.innerHTML = contentRef.current;
    }
  }, [isTyping, type, onTypingComplete]);

  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`flex ${type === 'user' ? 'justify-end' : 'justify-start'} mb-2`}
    >
      <div
        className={`max-w-[85%] p-2.5 rounded-lg text-sm ${
          type === 'user'
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted/50 backdrop-blur-sm'
        }`}
      >
        <div 
          ref={messageContentRef} 
          className="min-h-[1.5rem] whitespace-pre-wrap prose prose-sm dark:prose-invert max-w-none"
        />
      </div>
    </motion.div>
  );
});

ChatMessage.displayName = 'ChatMessage';